import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    DropdownSelect,
    components,
    type SingleValue,
    type OptionProps,
    type MenuProps,
    type GroupBase,
    type CSSObjectWithLabel,
} from 'src/components/DropdownWrapper/DropdownWrapper';
import { MenuOuterWrapper } from 'src/components/DropdownWrapper/DropdownWrapper.style';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import {
    CheckIconWrapper,
    OptionWrapper,
    Wrapper,
    Label,
    InputError,
    getCustomDropdownStyles,
} from './RealityCheckTab.style';

const errorMessages = (errorsForView: Array<string>): Array<JSX.Element> => {
    const out = [];

    for (const [index, message] of errorsForView.entries()) {
        out.push(
            <InputError
                key={index}
                data-test='input-error'
            >
                {message}
            </InputError>
        );
    }
    return out;
};

export type FilterValueType = string | number | undefined | null | symbol;

export type FilterOptionType<T> = {
    value: T;
    label: string | JSX.Element;
    disabled?: boolean;
};

interface CustomDropdownPropsType<T> {
    optionsList: () => Array<FilterOptionType<T>>;
    selectedValue: () => T;
    onChangeCallback: (selectedValue: T) => void;
    placeholder?: string;
    dataTest?: string;
    labelHeader?: string;
    formInputState?: FormInputState<string, unknown>;
    className?: string;
    /** When true, menu renders in document flow so the parent cell can grow (e.g. table rows). Default: false. */
    inlineMenu?: boolean;
    /** When false, placeholder/value are vertically centered for external labels. Default: true. */
    hasInternalLabel?: boolean;
    /** When true, menu renders in a portal to avoid clipping by overflow-hidden ancestors. Default: false. */
    useMenuPortal?: boolean;
    disabled?: boolean;
}

export const CustomDropdown = observer(
    'CustomDropdown',
    <T extends string | number | undefined | null | symbol>(props: CustomDropdownPropsType<T>) => {
        const {
            placeholder,
            dataTest,
            optionsList,
            selectedValue,
            onChangeCallback,
            labelHeader,
            formInputState,
            className,
            inlineMenu = false,
            hasInternalLabel = true,
            useMenuPortal = false,
            disabled = false,
        } = props;

        const optionsListToDisplay = optionsList();
        const activeFilterElement = optionsListToDisplay.find((option) => option.value === selectedValue()) ?? null;

        const onChangeTrigger = (option: SingleValue<FilterOptionType<T>>): void => {
            if (option === null) {
                console.error('DropdownFilter - filter onChange error');
                return;
            }

            onChangeCallback(option.value);
        };

        const dropdownStyles = React.useMemo(() => {
            const styles = getCustomDropdownStyles<FilterOptionType<T>, false>();

            if (!useMenuPortal) {
                return styles;
            }

            return {
                ...styles,
                menuPortal: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
                    ...base,
                    zIndex: 9999,
                }),
            };
        }, [useMenuPortal]);

        const CustomOption = (
            optionProps: OptionProps<FilterOptionType<T>, false, GroupBase<FilterOptionType<T>>>
        ): JSX.Element => {
            const { data, innerRef, innerProps, isSelected } = optionProps;

            return (
                <OptionWrapper
                    ref={innerRef}
                    {...innerProps}
                    isSelected={isSelected}
                    role='option'
                    aria-selected={isSelected}
                >
                    {data.label}
                    {isSelected ? <CheckIconWrapper /> : null}
                </OptionWrapper>
            );
        };

        const InlineMenu = (
            menuProps: MenuProps<FilterOptionType<T>, false, GroupBase<FilterOptionType<T>>>
        ): JSX.Element => (
            <MenuOuterWrapper className='net-deposit-inline-menu'>
                <components.Menu {...menuProps}>{menuProps.children}</components.Menu>
            </MenuOuterWrapper>
        );

        return (
            <Wrapper data-test={dataTest}>
                <DropdownSelect<FilterOptionType<T>>
                    className={`custom-dropdown-select ${className ?? ''}`}
                    placeholder={placeholder}
                    onChange={onChangeTrigger}
                    options={optionsListToDisplay}
                    value={activeFilterElement}
                    components={inlineMenu ? { Option: CustomOption, Menu: InlineMenu } : { Option: CustomOption }}
                    customStyles={dropdownStyles}
                    getOptionValue={(option) => String(option.value)}
                    getOptionLabel={(option) => (typeof option.label === 'string' ? option.label : '')}
                    hasInternalLabel={hasInternalLabel}
                    isDisabled={disabled}
                    menuPortalTarget={useMenuPortal ? document.body : undefined}
                    menuPosition={useMenuPortal ? 'fixed' : undefined}
                />
                {hasInternalLabel && labelHeader !== undefined ? <Label>{labelHeader}</Label> : null}
                {formInputState === undefined ? null : errorMessages(formInputState.errorsForView)}
            </Wrapper>
        );
    }
);
